import axios from "axios";
import { useContext, useRef } from "react";
import { Context } from "./Reducers/Store";
import { useTokenHelper } from "./Hooks/useTokenHelper";
import { apiUrl, getPayload } from "./services/apiConfig";

export const WithAxios = ({ children }) => {
  const [, dispatch] = useContext(Context);
  const requests = useRef(0);
  const tokenHelper = useTokenHelper();

  if (window._agendadorInterceptorRequest != null) {
    axios.interceptors.request.eject(window._agendadorInterceptorRequest);
  }

  if (window._agendadorInterceptorResponse != null) {
    axios.interceptors.response.eject(window._agendadorInterceptorResponse);
  }

  window._agendadorInterceptorRequest = axios.interceptors.request.use(
    async (request) => {
      const jwtToken = await tokenHelper.getToken();
      const isAuthEndpoint = request?.url?.includes("/auth/login") ||
        request?.url?.includes("/auth/refresh") ||
        request?.url?.includes("/auth/primeiro-admin");

      if (jwtToken && !isAuthEndpoint) {
        request.headers.Authorization = `Bearer ${jwtToken}`;
      }

      if (!request.metadata?.ignoreLoading) {
        dispatch({ type: "SET_LOADING", payload: true });
        requests.current += 1;
      }

      return request;
    },
    (error) => Promise.reject(error)
  );

  window._agendadorInterceptorResponse = axios.interceptors.response.use(
    (response) => {
      if (!response.config?.metadata?.ignoreLoading) {
        requests.current -= 1;
        if (requests.current <= 0) {
          requests.current = 0;
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }

      return response;
    },
    async (error) => {
      if (!error.config?.metadata?.ignoreLoading) {
        requests.current -= 1;
        if (requests.current <= 0) {
          requests.current = 0;
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }

      const status = error?.response?.status ?? error?.status;
      const originalRequest = error?.config ?? {};
      const isLoginEndpoint = originalRequest?.url?.includes("/auth/login");
      const isRefreshEndpoint = originalRequest?.url?.includes("/auth/refresh");
      const isFirstAdminEndpoint = originalRequest?.url?.includes("/auth/primeiro-admin");

      if (status === 401 && !isLoginEndpoint && !isRefreshEndpoint && !isFirstAdminEndpoint) {
        const refreshToken = localStorage.getItem("refreshToken");

        if (refreshToken && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            if (!window._agendadorRefreshPromise) {
              window._agendadorRefreshPromise = axios
                .post(apiUrl("/auth/refresh"), { refreshToken }, { metadata: { ignoreLoading: true } })
                .then((response) => getPayload(response))
                .finally(() => {
                  window._agendadorRefreshPromise = null;
                });
            }

            const payload = await window._agendadorRefreshPromise;
            dispatch({ type: "SET_LOGGED", payload });

            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers.Authorization = `Bearer ${payload.token}`;

            return axios(originalRequest);
          } catch {
            dispatch({ type: "SET_LOGGED", payload: null });
            error.message = "SESSION_EXPIRED";
          }
        } else {
          dispatch({ type: "SET_LOGGED", payload: null });
          error.message = "SESSION_EXPIRED";
        }
      }

      return Promise.reject(error);
    }
  );

  return children;
};
